import { useState, useEffect } from 'react';
import Button from './Button';
import { Toast } from './Toast';
import { X } from 'lucide-react';
import { RoleService } from '../../services/RoleService';
import Select from './Select'; // Import the new Select component

const styles = {
	section: 'fixed inset-0 z-[9999] flex items-center justify-center p-4',
	backDrop: 'absolute inset-0 bg-black/70',
	modal: 'relative z-10 w-full max-w-[500px] rounded-[18px] bg-white shadow-2xl p-[30px]',
	heading: 'text-25 font-bold text-dark-blue mb-3 leading-100',
	subHeading: 'text-gray text-sm mb-8',
	label: 'text-base leading-100 font-medium text-dark-blue'
};

export default function SubAdminModal({
	isOpen,
	onClose,
	isEdit = false,
	initialFirstName = '',
	initialLastName = '',
	initialEmail = '',
	initialRole = '', // New prop for initial role
	onAddSubAdmin, // New prop for adding a sub-admin
	onUpdateSubAdmin, // New prop for updating a sub-admin (now handles deactivation)
	userId, // New prop for user ID
	currentStatus // New prop to determine activate/deactivate button text and action
}) {
	const [firstName, setFirstName] = useState(initialFirstName);
	const [lastName, setLastName] = useState(initialLastName);
	const [email, setEmail] = useState(initialEmail);
	const [isLoading, setIsLoading] = useState(false); // New loading state for submit button
	const [isStatusChanging, setIsStatusChanging] = useState(false); // New loading state for activate/deactivate button
	const [roles, setRoles] = useState([]); // State to store fetched roles
	const [selectedRole, setSelectedRole] = useState(''); // State for selected role

	// Function to reset all relevant states
	const resetStates = () => {
		setFirstName('');
		setLastName('');
		setEmail('');
		setSelectedRole('');
		setIsLoading(false);
		setIsStatusChanging(false);
	};

	// Effect to update internal state when initial props change or modal opens/closes
	useEffect(() => {
		if (isOpen) {
			setFirstName(initialFirstName);
			setLastName(initialLastName);
			setEmail(initialEmail);
			// Removed selectedRole setting from here, it will be handled after roles are fetched
		} else {
			resetStates();
		}
	}, [isOpen, initialFirstName, initialLastName, initialEmail]); // Removed isEdit, initialRole from dependencies as selectedRole is set later

	useEffect(() => {
		const fetchRoles = async () => {
			try {
				const response = await RoleService.getRoles();
				setRoles(response.data.roles);

				// Ensure initialRole is set AFTER roles are fetched for edit mode
				if (isEdit && initialRole) {
					const matchedRole = response.data.roles.find((role) => role.roleType === initialRole);
					if (matchedRole) {
						setSelectedRole(matchedRole._id);
					} else {
						setSelectedRole(''); // Fallback if initial roleType not found
					}
				} else if (!isEdit) {
					setSelectedRole(''); // Ensure no role is selected by default in add mode
				}
			} catch (error) {
				console.error('Error fetching roles:', error);
			}
		};

		if (isOpen) {
			fetchRoles();
		}
	}, [isOpen, isEdit, initialRole]); // Dependencies include initialRole for pre-selection

	if (!isOpen) return null;

	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);

		const trimmedFirstName = firstName.trim();
		const trimmedLastName = lastName.trim();
		const trimmedEmail = email.trim();
		const nameRegex = /^[A-Za-z\s]+$/;
		const isValidEmail = (value) => /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/.test(value);

		// First name validation
		if (!trimmedFirstName) {
			Toast.error('Please enter your first name.');
			setIsLoading(false);
			return;
		}
		if (trimmedFirstName.length < 3) {
			Toast.error('Minimum length for first name is 3 characters.');
			setIsLoading(false);
			return;
		}
		if (!nameRegex.test(trimmedFirstName)) {
			Toast.error('First name can only include letters.');
			setIsLoading(false);
			return;
		}

		// Last name validation
		if (!trimmedLastName) {
			Toast.error('Please enter your last name.');
			setIsLoading(false);
			return;
		}
		if (trimmedLastName.length < 3) {
			Toast.error('Minimum length for last name is 3 characters.');
			setIsLoading(false);
			return;
		}
		if (!nameRegex.test(trimmedLastName)) {
			Toast.error('Last name can only include letters.');
			setIsLoading(false);
			return;
		}

		// Role validation
		if (!selectedRole) {
			Toast.error('Please select a role.');
			setIsLoading(false);
			return;
		}

		// Email validations only when adding (email is disabled on edit)
		if (!isEdit) {
			if (!trimmedEmail) {
				Toast.error('Please enter your email address.');
				setIsLoading(false);
				return;
			}
			if (!isValidEmail(trimmedEmail)) {
				Toast.error('Please enter a valid email address (e.g.,name@email.com).');
				setIsLoading(false);
				return;
			}
		}

		if (isEdit) {
			if (onUpdateSubAdmin && userId) {
				const updatedFields = {};

				if (trimmedFirstName !== initialFirstName) {
					updatedFields.firstName = trimmedFirstName;
				}
				if (trimmedLastName !== initialLastName) {
					updatedFields.lastName = trimmedLastName;
				}

				// If role has changed, include it in updatedFields
				if (selectedRole !== initialRole) {
					const selectedRoleObject = roles.find((role) => role._id === selectedRole);
					if (!selectedRoleObject) {
						Toast.error('Selected role not found!');
						setIsLoading(false);
						return;
					}
					updatedFields.roleId = selectedRoleObject._id;
					updatedFields.effectivePermissions = selectedRoleObject.permissions;
				}

				// Only call update if there are actual changes
				if (Object.keys(updatedFields).length > 0) {
					const success = await onUpdateSubAdmin(userId, updatedFields);
					if (success) {
						onClose();
					}
				} else {
					// If no changes, just close the modal
					onClose();
				}
			}
		} else {
			// Handle add logic
			if (!firstName.trim()) {
				Toast.error('Please enter the first name.');
				setIsLoading(false);
				return;
			}

			if (firstName.trim().length < 3) {
				Toast.error('Minimum length for first name is 3 characters');
				setIsLoading(false);
				return;
			}

			if (!lastName.trim()) {
				Toast.error('Please enter the last name.');
				setIsLoading(false);
				return;
			}

			if (lastName.trim().length < 3) {
				Toast.error('Minimum length for last name is 3 characters');
				setIsLoading(false);
				return;
			}

			if (!email.trim()) {
				Toast.error('Please enter the email address.');
				setIsLoading(false);
				return;
			}

			// Basic email format validation
			const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
			if (!emailRegex.test(email)) {
				Toast.error('Please enter a valid email address.');
				setIsLoading(false);
				return;
			}

			if (onAddSubAdmin) {
				const selectedRoleObject = roles.find((role) => role._id === selectedRole);
				if (!selectedRoleObject) {
					Toast.error('Please select a role');
					setIsLoading(false);
					return;
				}
				const success = await onAddSubAdmin({
					firstName: trimmedFirstName,
					lastName: trimmedLastName,
					email: trimmedEmail,
					roleId: selectedRoleObject._id,
					effectivePermissions: selectedRoleObject.permissions
				});
				if (success) {
					setFirstName(''); // Clear states on success
					setLastName('');
					setEmail('');
					onClose(); // Close modal only on success
				}
			}
		}
		setIsLoading(false); // Re-enable inputs after API call
	};

	const handleStatusChangeClick = async () => {
		if (onUpdateSubAdmin && userId) {
			setIsStatusChanging(true);
			const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
			const success = await onUpdateSubAdmin(userId, { status: newStatus }); // Pass new status
			if (success) {
				onClose();
			}
			setIsStatusChanging(false);
		}
	};

	const modalTitle = isEdit ? 'Edit User' : 'Add User';
	const modalSubHeading = isEdit
		? 'Update user infromation'
		: 'Assign limited admin access to a team member.';
	const firstNamePlaceholder = isEdit ? 'Zunair' : 'John';
	const lastNamePlaceholder = isEdit ? 'Khan' : 'Doe';
	const emailPlaceholder = isEdit ? 'zunairkhan@nesl-it.com' : 'john.doe@example.com';

	return (
		<div className={styles.section} onClick={handleBackdropClick}>
			{/* Backdrop */}
			<div className={styles.backDrop} />

			{/* Modal */}
			<div className={styles.modal}>
				<button
					onClick={onClose} // This will trigger the useEffect to reset states
					className="absolute top-8 right-4 md:right-6 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
				>
					<X className="w-3 h-3 text-white" />
				</button>

				{/* Heading */}
				<h2 className={styles.heading}>{modalTitle}</h2>
				<p className={styles.subHeading}>{modalSubHeading}</p>

				{/* Input fields */}
				<div className="grid grid-cols-1 gap-6 mb-5">
					{/* Role Selection */}
					<div>
						<div className="flex items-center gap-2 mb-3">
							<label htmlFor="role-select" className={styles.label}>
								Select Role <span className="text-red-500">*</span>
							</label>
						</div>
						<Select
							id="role-select"
							value={selectedRole}
							onChange={(e) => setSelectedRole(e.target.value)}
							disabled={isLoading || isStatusChanging}
							options={
								isEdit
									? roles.map((role) => ({ label: role.roleType, value: role._id }))
									: [...roles.map((role) => ({ label: role.roleType, value: role._id }))]
							}
						/>
					</div>
					{/* First Name */}
					<div>
						<div className="flex items-center gap-2 mb-3">
							<label htmlFor="first-name" className={styles.label}>
								First Name <span className="text-red-500">*</span>
							</label>
						</div>
						<input
							id="first-name"
							type="text"
							placeholder={firstNamePlaceholder}
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							disabled={isLoading} // Disable input
							className="w-full rounded-[10px] border-1p3 border-lightGrey px-4 py-3 text-base disabled:bg-gray-100 disabled:text-gray-500"
						/>
					</div>

					{/* Last Name */}
					<div>
						<div className="flex items-center gap-2 mb-3">
							<label htmlFor="last-name" className={styles.label}>
								Last Name <span className="text-red-500">*</span>
							</label>
						</div>
						<input
							id="last-name"
							type="text"
							placeholder={lastNamePlaceholder}
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							disabled={isLoading} // Disable input
							className="w-full rounded-[10px] border-1p3 border-lightGrey px-4 py-3 text-base disabled:bg-gray-100 disabled:text-gray-500"
						/>
					</div>

					{/* Email Address */}
					<div>
						<div className={`flex items-center gap-2 mb-3 ${isEdit ? 'opacity-50' : ''}`}>
							<label htmlFor="email-address" className={styles.label}>
								Email Address <span className="text-red-500">*</span>
							</label>
						</div>
						<input
							id="email-address"
							type="email"
							placeholder={emailPlaceholder}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							disabled={isLoading || isEdit} // Disable input (also if in edit mode)
							className="w-full rounded-[10px] border-1p3 border-lightGrey px-4 py-3 text-base disabled:bg-gray-100 disabled:text-gray-500"
						/>
					</div>
				</div>

				{/* Buttons */}
				<div className="flex gap-3 flex-col">
					<Button
						type="submit"
						onClick={handleSubmit}
						disabled={isLoading || isStatusChanging}
						className="disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isLoading
							? isEdit
								? 'Updating...'
								: 'Adding User...'
							: isEdit
							? 'Update User'
							: 'Add User'}
					</Button>
					{isEdit && (
						<Button
							variant="outline-gradient-text"
							onClick={handleStatusChangeClick}
							disabled={isStatusChanging || isLoading}
						>
							{isStatusChanging
								? currentStatus === 'active'
									? 'Deactivating...'
									: 'Activating...'
								: currentStatus === 'active'
								? 'Deactivate User'
								: 'Activate User'}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
